import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowUpRight } from 'lucide-react';

import { Container } from '@/components/ui/container';
import { Logo } from '@/components/ui/logo';
import { XIcon, LinkedInIcon } from '@/components/ui/brand-icons';
import { footer, links } from '@/content/site';
import { trackDocsClick } from '@/lib/analytics';

const SOCIAL_CLASS =
  'grid size-9 place-items-center rounded-lg text-ink-700 transition-colors hover:border-ink-300 hover:text-ink-900 hover:bg-surface';

const LINK_CLASS =
  'group inline-flex items-center gap-1 text-sm text-ink-600 transition-colors hover:text-slate-950';

export function Footer() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-hairline bg-white">
      <Container className="py-14 sm:py-16">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,13rem)_minmax(0,1fr)]">
          <div>
            <Logo />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-ink-500">
              {footer.tagline}
            </p>
            <Link
              to="/blog"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-ink-700 transition-colors hover:text-ember-700"
            >
              Read the Den blog <ArrowUpRight className="size-3.5" />
            </Link>
            <div className="mt-6 flex items-center gap-3">
              <a
                href={links.x}
                target="_blank"
                rel="noreferrer"
                aria-label="Den on X"
                className={SOCIAL_CLASS}
              >
                <XIcon className="size-4" />
              </a>
              <a
                href={links.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="Den on LinkedIn"
                className={SOCIAL_CLASS}
              >
                <LinkedInIcon className="size-4" />
              </a>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
            {footer.columns.map((col) => (
              <div key={col.label}>
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-400">
                  {col.label}
                </h3>
                <ul className="mt-4 flex flex-col gap-3">
                  {col.items.map((link) => (
                    <li key={link.label}>
                      {link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                          className={LINK_CLASS}
                          onClick={() => trackDocsClick(link.href, 'footer')}
                        >
                          {link.label}
                          <ArrowUpRight className="size-3.5 text-ink-300 transition-colors group-hover:text-ink-700" />
                        </a>
                      ) : (
                        <Link to={link.href} className={LINK_CLASS}>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-hairline pt-8 text-sm text-ink-400 sm:flex-row">
          {/* Case: across New Year, hydration can leave the build's year in the
              DOM. Remount this text after hydration; suppression alone does not
              update it, even when a later render computes the current year. */}
          <span key={mounted ? 'client' : 'static'} suppressHydrationWarning>
            © {year} Den Technologies. All rights reserved.
          </span>
          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link
              to="/privacy"
              className="transition-colors hover:text-ink-700"
            >
              Privacy
            </Link>
            <Link to="/terms" className="transition-colors hover:text-ink-700">
              Terms
            </Link>
            <a
              href={links.demo}
              className="transition-colors hover:text-ink-700"
            >
              Contact
            </a>
          </div>
        </div>
      </Container>
    </footer>
  );
}
